import React from 'react';
import { Link } from 'react-router-dom';

const StockMarketFundamentals = () => {
  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4">📊 Stock Market Fundamentals</h1>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>What is the Stock Market?</h3>
              <p>
                The stock market is a collection of markets where stocks (pieces of ownership in businesses) 
                are traded between investors. It usually refers to the exchanges where stocks and other 
                securities are bought and sold.
              </p>
              <p>
                Think of it as a giant marketplace where instead of buying fruits or vegetables, 
                people buy and sell pieces of companies. When you buy a stock, you're buying a 
                small piece of that company and becoming a shareholder.
              </p>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  When Apple went public in 1980, its initial public offering (IPO) price was $22 per share. 
                  If you had invested $1,000 (about 45 shares) and held until today (with stock splits), 
                  your investment would be worth over $1.5 million. This demonstrates how owning a small piece 
                  of a successful company through stocks can create significant wealth over time.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Market Participants</h3>
              <div className="row">
                <div className="col-md-6">
                  <h5>Individual Investors</h5>
                  <p>Regular people like you and me who buy and sell stocks for personal investment.</p>
                  <p className="small text-muted">
                    Individual investors typically trade through brokerage platforms like Robinhood, 
                    E*TRADE, or Charles Schwab, and make up about 25% of market activity.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Institutional Investors</h5>
                  <p>Large organizations like pension funds, mutual funds, and insurance companies.</p>
                  <p className="small text-muted">
                    These entities manage trillions of dollars and can significantly impact stock prices 
                    when they buy or sell large positions. They account for roughly 75% of trading volume.
                  </p>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Market Makers</h5>
                  <p>Companies that provide liquidity by always being ready to buy or sell stocks.</p>
                  <p className="small text-muted">
                    Firms like Citadel Securities and Virtu Financial facilitate millions of trades daily, 
                    ensuring you can buy or sell stocks instantly at competitive prices.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Brokers</h5>
                  <p>Intermediaries who execute buy and sell orders for investors.</p>
                  <p className="small text-muted">
                    Modern brokers like TD Ameritrade and Interactive Brokers not only execute trades but 
                    also provide research tools, educational resources, and sometimes financial advice.
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In January 2021, individual investors coordinating on Reddit's WallStreetBets forum 
                  collectively purchased GameStop (GME) shares, driving the price from $17 to nearly $350. 
                  This demonstrated how even individual investors, when acting together, can influence 
                  market dynamics traditionally dominated by institutions.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stock Exchanges</h3>
              <p>
                Stock exchanges are organized markets where stocks are traded. The most famous ones include:
              </p>
              <ul>
                <li>
                  <strong>New York Stock Exchange (NYSE):</strong> The largest stock exchange in the world
                  <p className="small text-muted">
                    Founded in 1792, the NYSE lists over 2,300 companies with a combined market capitalization 
                    exceeding $25 trillion. Companies like Walmart, Coca-Cola, and Disney trade here.
                  </p>
                </li>
                <li>
                  <strong>NASDAQ:</strong> Known for technology companies like Apple, Microsoft, and Google
                  <p className="small text-muted">
                    Started in 1971 as the world's first electronic stock market, NASDAQ hosts most major 
                    tech companies and is known for its advanced trading technology.
                  </p>
                </li>
                <li>
                  <strong>London Stock Exchange (LSE):</strong> One of the oldest exchanges in the world
                  <p className="small text-muted">
                    Dating back to 1698, the LSE is Europe's largest stock exchange and a global 
                    financial hub, listing companies from over 60 countries.
                  </p>
                </li>
                <li>
                  <strong>Tokyo Stock Exchange (TSE):</strong> The largest exchange in Asia
                  <p className="small text-muted">
                    Home to major Japanese corporations like Toyota, Sony, and SoftBank, the TSE 
                    plays a crucial role in the Asian financial ecosystem.
                  </p>
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  When Facebook (now Meta) went public in 2012, it chose to list on NASDAQ rather than NYSE. 
                  The IPO was priced at $38 per share, valuing the company at $104 billion - the largest 
                  valuation for a newly public company at that time. However, technical glitches on NASDAQ 
                  delayed trading and caused confusion among investors, demonstrating how the choice of 
                  exchange can impact a company's public debut.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>How Stock Prices Are Determined</h3>
              <p>
                Stock prices are determined by supply and demand in the market. When more people want to 
                buy a stock than sell it, the price goes up. When more people want to sell than buy, the 
                price goes down.
              </p>
              <p>
                Several factors influence these buying and selling decisions:
              </p>
              <ul>
                <li>
                  <strong>Company Performance:</strong> Earnings reports, revenue growth, and profit margins
                </li>
                <li>
                  <strong>Industry Trends:</strong> How the company's sector is performing overall
                </li>
                <li>
                  <strong>Economic Indicators:</strong> Interest rates, inflation, GDP growth
                </li>
                <li>
                  <strong>Market Sentiment:</strong> Investor confidence and emotional reactions
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In February 2023, Meta (Facebook) reported better-than-expected earnings and announced a 
                  $40 billion stock buyback program. The stock jumped 23% in a single day, adding over $100 
                  billion to the company's market value. This illustrates how company performance and actions 
                  directly impact stock prices through supply and demand dynamics.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Initial Public Offerings (IPOs)</h3>
              <p>
                An Initial Public Offering (IPO) is the process through which a private company offers shares to the public for the first time.
                This transition from private to public ownership is a significant milestone in a company's growth journey.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>The IPO Process</h5>
                  <ul>
                    <li>
                      <strong>Hiring Investment Banks:</strong> Companies select underwriters to help navigate the process
                    </li>
                    <li>
                      <strong>Due Diligence & Filing:</strong> Preparing financial statements and filing with regulators
                    </li>
                    <li>
                      <strong>Roadshow:</strong> Presenting to potential investors to generate interest
                    </li>
                    <li>
                      <strong>Pricing:</strong> Setting the initial share price based on demand
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Pros and Cons of IPOs</h5>
                  <ul>
                    <li>
                      <strong>Advantages:</strong> Access to capital, increased visibility, liquidity for early investors
                    </li>
                    <li>
                      <strong>Disadvantages:</strong> Expensive process, increased regulatory requirements, pressure for short-term results
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  When Airbnb went public in December 2020 during the pandemic, it initially set a price range of $44-$50 per share. 
                  Due to strong investor demand during its roadshow, it raised this to $56-$60. The stock ultimately priced at $68 and 
                  opened at $146 on its first trading day, demonstrating the sometimes unpredictable nature of IPO pricing and the 
                  significant public interest in innovative business models despite economic uncertainty.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stock Market Indices</h3>
              <p>
                Stock market indices are statistical measures that track the performance of a group of stocks, providing a snapshot of market performance.
                They serve as benchmarks for investors and reflect the health of different market segments.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Major Global Indices</h5>
                  <ul>
                    <li>
                      <strong>S&P 500:</strong> 500 large U.S. companies, market-cap weighted
                      <p className="small text-muted">
                        Considered the best representation of the U.S. stock market and economy
                      </p>
                    </li>
                    <li>
                      <strong>Dow Jones Industrial Average:</strong> 30 prominent U.S. companies, price-weighted
                      <p className="small text-muted">
                        The oldest and most widely quoted index, though less representative due to its small size
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Specialized Indices</h5>
                  <ul>
                    <li>
                      <strong>NASDAQ Composite:</strong> Technology-heavy index of over 3,000 companies
                    </li>
                    <li>
                      <strong>Russell 2000:</strong> 2,000 small-cap U.S. companies
                    </li>
                    <li>
                      <strong>Sector Indices:</strong> Track specific industries like healthcare or energy
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the COVID-19 pandemic in 2020, the S&P 500 and NASDAQ Composite showed dramatically different performances. 
                  While the S&P 500 gained about 16% for the year, the tech-heavy NASDAQ soared by approximately 43%. This divergence 
                  highlighted how different market segments can perform very differently during the same economic conditions, with 
                  technology companies benefiting from the accelerated digital transformation while many traditional businesses struggled.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Market Cycles</h3>
              <p>
                Stock markets move in cycles, alternating between periods of expansion (bull markets) and contraction (bear markets).
                Understanding these cycles can help investors make more informed decisions.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Bull Market Characteristics</h5>
                  <ul>
                    <li>Rising stock prices (typically 20%+ from recent lows)</li>
                    <li>Strong economic indicators</li>
                    <li>High investor confidence</li>
                    <li>Typically lasts 2-5 years on average</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Bear Market Characteristics</h5>
                  <ul>
                    <li>Falling stock prices (typically 20%+ from recent highs)</li>
                    <li>Economic slowdown or recession</li>
                    <li>Investor pessimism</li>
                    <li>Typically lasts 9-16 months on average</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Market Cycle Phases</h5>
              <ol>
                <li><strong>Accumulation:</strong> Smart money begins buying after a market bottom</li>
                <li><strong>Mark-Up:</strong> Prices trend upward as more investors enter</li>
                <li><strong>Distribution:</strong> Smart money begins selling to take profits</li>
                <li><strong>Mark-Down:</strong> Prices trend downward as selling accelerates</li>
              </ol>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  The bull market that began after the 2008 financial crisis lasted nearly 11 years (March 2009 to February 2020), 
                  the longest in U.S. history. During this period, the S&P 500 gained over 400%. It was followed by a brief but 
                  intense bear market in early 2020 due to the COVID-19 pandemic, where markets fell approximately 34% in just 
                  over a month. This rapid cycle demonstrated how external shocks can quickly end even the longest bull markets.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Market Capitalization</h3>
              <p>
                Market capitalization (or "market cap") is the total value of a company's outstanding shares, calculated by 
                multiplying the current share price by the number of shares outstanding. It's a key metric for categorizing companies.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-4">
                  <h5>Large-Cap Companies</h5>
                  <p>Market cap over $10 billion</p>
                  <p className="small text-muted">
                    Examples: Apple, Microsoft, Amazon
                  </p>
                  <ul>
                    <li>Generally more stable</li>
                    <li>Often pay dividends</li>
                    <li>Lower growth potential</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h5>Mid-Cap Companies</h5>
                  <p>Market cap $2-10 billion</p>
                  <p className="small text-muted">
                    Examples: Etsy, Roku, DocuSign
                  </p>
                  <ul>
                    <li>Balance of stability and growth</li>
                    <li>Often in expansion phase</li>
                    <li>Moderate risk profile</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h5>Small-Cap Companies</h5>
                  <p>Market cap $300 million-$2 billion</p>
                  <p className="small text-muted">
                    Examples: Early-stage tech companies, regional businesses
                  </p>
                  <ul>
                    <li>Higher growth potential</li>
                    <li>More volatile</li>
                    <li>Less analyst coverage</li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Tesla's market cap journey illustrates the dramatic impact of growth on valuation. In early 2020, Tesla was 
                  a mid-cap company valued around $80 billion. By the end of 2020, it had grown to over $600 billion, making it 
                  one of the largest companies in the world. This 750% increase in market cap in a single year demonstrated how 
                  rapidly a company can move between market cap categories when growth accelerates and investor sentiment shifts.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Types of Stocks</h3>
              <p>
                Stocks come in various types, each with different characteristics and benefits for investors.
                Understanding these differences can help you build a more balanced portfolio.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Common vs. Preferred Stock</h5>
                  <ul>
                    <li>
                      <strong>Common Stock:</strong> Provides voting rights but lower priority for dividends and assets
                      <p className="small text-muted">
                        Most widely traded type of stock, offering potential for capital appreciation
                      </p>
                    </li>
                    <li>
                      <strong>Preferred Stock:</strong> Higher claim on assets and earnings, fixed dividends, usually no voting rights
                      <p className="small text-muted">
                        Hybrid between common stock and bonds, offering more stable income
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>By Investment Style</h5>
                  <ul>
                    <li>
                      <strong>Growth Stocks:</strong> Companies expected to grow faster than average
                      <p className="small text-muted">
                        Often trade at higher valuations, reinvest profits rather than pay dividends
                      </p>
                    </li>
                    <li>
                      <strong>Value Stocks:</strong> Companies trading below their intrinsic value
                      <p className="small text-muted">
                        Often have lower P/E ratios and higher dividend yields
                      </p>
                    </li>
                    <li>
                      <strong>Dividend Stocks:</strong> Companies that regularly distribute earnings to shareholders
                      <p className="small text-muted">
                        Typically more established companies with stable cash flows
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In 2022, as interest rates rose and growth stocks fell out of favor, value stocks significantly outperformed. 
                  For instance, while technology-focused growth stocks in the NASDAQ fell by about 33%, value-oriented sectors 
                  like energy saw gains of over 50%. This illustrates how different types of stocks can perform very differently 
                  under changing economic conditions.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stock Market Regulations</h3>
              <p>
                Stock markets are regulated to protect investors, ensure fair trading, and maintain market integrity.
                Understanding these regulations helps investors navigate the market safely.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Key Regulatory Bodies</h5>
                  <ul>
                    <li>
                      <strong>Securities and Exchange Commission (SEC):</strong> Primary U.S. regulatory agency
                      <p className="small text-muted">
                        Enforces securities laws, requires public company disclosures, combats fraud
                      </p>
                    </li>
                    <li>
                      <strong>Financial Industry Regulatory Authority (FINRA):</strong> Self-regulatory organization
                      <p className="small text-muted">
                        Oversees broker-dealers and enforces rules for fair practices
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Important Regulations</h5>
                  <ul>
                    <li>
                      <strong>Insider Trading Laws:</strong> Prohibit trading based on non-public information
                    </li>
                    <li>
                      <strong>Circuit Breakers:</strong> Temporarily halt trading during extreme market volatility
                    </li>
                    <li>
                      <strong>Disclosure Requirements:</strong> Mandate regular financial reporting by public companies
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In March 2020, as COVID-19 fears gripped markets, circuit breakers were triggered multiple times in a single week. 
                  On March 16, the S&P 500 fell 7% shortly after the opening bell, triggering a 15-minute trading halt. This regulatory 
                  mechanism helped prevent panic selling and gave investors time to assess the situation, demonstrating how regulations 
                  can help stabilize markets during periods of extreme stress.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stock Splits and Buybacks</h3>
              <p>
                Companies can adjust their outstanding shares through corporate actions like stock splits and buybacks,
                which can impact share price and ownership structure without changing the company's fundamental value.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Stock Splits</h5>
                  <p>
                    A corporate action that increases the number of shares while proportionally decreasing the price per share.
                  </p>
                  <ul>
                    <li>
                      <strong>Forward Split:</strong> Increases share count (e.g., 2-for-1 split doubles shares, halves price)
                      <p className="small text-muted">
                        Makes shares more affordable and potentially more liquid
                      </p>
                    </li>
                    <li>
                      <strong>Reverse Split:</strong> Decreases share count (e.g., 1-for-10 split reduces shares by 90%, multiplies price by 10)
                      <p className="small text-muted">
                        Often used to increase share price to meet exchange listing requirements
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Share Buybacks</h5>
                  <p>
                    When a company repurchases its own shares from the open market.
                  </p>
                  <ul>
                    <li>
                      <strong>Benefits:</strong> Reduces outstanding shares, potentially increasing EPS and share price
                    </li>
                    <li>
                      <strong>Signals:</strong> May indicate management believes shares are undervalued
                    </li>
                    <li>
                      <strong>Alternative to Dividends:</strong> More tax-efficient way to return capital to shareholders
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In August 2020, Apple conducted a 4-for-1 stock split, reducing its share price from around $500 to $125. 
                  This made the stock more accessible to retail investors. In the month following the split announcement, 
                  Apple's stock rose by about 30%, demonstrating how splits can increase demand despite not changing the 
                  company's fundamental value. Meanwhile, Apple also continued its aggressive buyback program, repurchasing 
                  over $90 billion worth of shares in 2021 alone.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Market Sentiment and Psychology</h3>
              <p>
                Market sentiment refers to the overall attitude of investors toward a particular security or market.
                Understanding market psychology can help investors avoid emotional decisions and identify opportunities.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Common Psychological Biases</h5>
                  <ul>
                    <li>
                      <strong>Herd Mentality:</strong> Following the crowd rather than independent analysis
                    </li>
                    <li>
                      <strong>Loss Aversion:</strong> Feeling losses more strongly than equivalent gains
                    </li>
                    <li>
                      <strong>Recency Bias:</strong> Giving too much weight to recent events
                    </li>
                    <li>
                      <strong>Confirmation Bias:</strong> Seeking information that confirms existing beliefs
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Sentiment Indicators</h5>
                  <ul>
                    <li>
                      <strong>VIX (Fear Index):</strong> Measures expected market volatility
                    </li>
                    <li>
                      <strong>Put/Call Ratio:</strong> Compares bearish to bullish options activity
                    </li>
                    <li>
                      <strong>Bull/Bear Surveys:</strong> Gauge investor sentiment through polls
                    </li>
                    <li>
                      <strong>Fund Flows:</strong> Track money moving in and out of different assets
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In March 2020, as COVID-19 fears peaked, the VIX reached an all-time high of 82.69, indicating extreme fear. 
                  The S&P 500 had fallen 34% from its peak. However, this extreme pessimism marked a buying opportunity, as 
                  markets subsequently rebounded strongly. By August 2020, the S&P 500 had recovered all its losses, demonstrating 
                  how contrarian thinking during periods of extreme sentiment can lead to profitable investment decisions.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Global Market Interconnections</h3>
              <p>
                Today's stock markets are globally interconnected, with events in one region often impacting markets worldwide.
                Understanding these relationships helps investors anticipate market movements and diversify effectively.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Market Correlations</h5>
                  <ul>
                    <li>
                      <strong>Developed Markets:</strong> Tend to move together, especially during crises
                      <p className="small text-muted">
                        U.S., European, and Japanese markets often show high correlation
                      </p>
                    </li>
                    <li>
                      <strong>Emerging Markets:</strong> Sometimes offer diversification benefits
                      <p className="small text-muted">
                        May be more influenced by local factors than global trends
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Global Factors</h5>
                  <ul>
                    <li>
                      <strong>Currency Movements:</strong> Impact multinational companies and international investments
                    </li>
                    <li>
                      <strong>Central Bank Policies:</strong> Interest rate decisions affect global capital flows
                    </li>
                    <li>
                      <strong>Geopolitical Events:</strong> Wars, trade disputes, and political changes create market ripples
                    </li>
                    <li>
                      <strong>Commodity Prices:</strong> Oil, gold, and other commodities influence global markets
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  When the United Kingdom voted to leave the European Union (Brexit) in June 2016, the immediate impact was felt globally. 
                  The British pound fell to a 31-year low, the FTSE 100 dropped 3.2%, Japan's Nikkei fell 7.9%, and the S&P 500 fell 3.6%. 
                  However, while the FTSE 100 recovered quickly due to its many multinational companies that benefit from a weaker pound, 
                  the more domestically focused FTSE 250 took much longer to recover, demonstrating how global events can have different 
                  impacts across markets and sectors.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3>Key Takeaways</h3>
              <div className="alert alert-info">
                <ul className="mb-0">
                  <li>The stock market is where pieces of companies are bought and sold</li>
                  <li>Many different types of participants make the market work</li>
                  <li>Stock exchanges provide organized, regulated places for trading</li>
                  <li>Stock prices are determined by supply and demand</li>
                  <li>IPOs represent a company's transition from private to public ownership</li>
                  <li>Market indices track the performance of different segments of the market</li>
                  <li>Markets move in cycles between bull and bear phases</li>
                  <li>Market capitalization helps categorize companies by size and characteristics</li>
                  <li>Different types of stocks (common, preferred, growth, value) serve different investment needs</li>
                  <li>Regulations protect investors and ensure fair, orderly markets</li>
                  <li>Corporate actions like splits and buybacks affect share structure without changing fundamental value</li>
                  <li>Market psychology often drives short-term price movements</li>
                  <li>Global markets are interconnected, with events in one region affecting others</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/investment-basics" className="btn btn-primary-custom">
              Next: Investment Basics →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMarketFundamentals;
